import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Ticket,
    Tag,
    Calendar,
    Users,
    BarChart3,
    MessageSquare,
    CreditCard,
    Send,
    LogOut,
    Terminal,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const NAV = [
    { to: "/", label: "Dashboard", Icon: LayoutDashboard, exact: true },
    { to: "/tickets", label: "Tickets", Icon: Ticket },
    { to: "/analytics", label: "Analytics", Icon: BarChart3 },
    { to: "/categories", label: "Categories", Icon: Tag, admin: true },
    { to: "/holidays", label: "Holidays", Icon: Calendar, admin: true },
    { to: "/team", label: "Team", Icon: Users, admin: true },
];

const SIM_NAV = [
    { to: "/simulator/whatsapp", label: "WhatsApp", Icon: MessageSquare },
    { to: "/simulator/payment", label: "Payments", Icon: CreditCard },
    { to: "/simulator/telegram", label: "Telegram", Icon: Send },
];

export default function AppShell({ children }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [mockMode, setMockMode] = useState(true);

    useEffect(() => {
        api.get("/config")
            .then((r) => setMockMode(!!r.data.mock_mode))
            .catch(() => {});
    }, []);

    const Item = ({ to, label, Icon, exact }) => (
        <NavLink
            to={to}
            end={exact}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors border border-transparent ${
                    isActive
                        ? "bg-secondary text-foreground border-border"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`
            }
        >
            <Icon className="size-4" />
            <span>{label}</span>
        </NavLink>
    );

    return (
        <div className="min-h-screen flex">
            <aside className="w-60 shrink-0 border-r border-border bg-[hsl(240,3%,5%)] flex flex-col">
                <div className="px-4 py-5 border-b border-border flex items-center gap-2">
                    <div className="size-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
                        <Terminal className="size-4" />
                    </div>
                    <div className="leading-tight">
                        <div className="text-sm font-semibold tracking-wide">OMX</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                            Support Console
                        </div>
                    </div>
                </div>
                <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
                    {NAV.filter((n) => !n.admin || user?.role === "ADMIN").map((n) => (
                        <Item key={n.to} {...n} />
                    ))}
                    {mockMode && (
                        <>
                            <div className="mt-5 mb-1 px-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                Simulators
                            </div>
                            {SIM_NAV.map((n) => (
                                <Item key={n.to} {...n} />
                            ))}
                        </>
                    )}
                </nav>
                <div className="p-3 border-t border-border">
                    <div className="text-xs font-medium">{user?.name}</div>
                    <div className="text-[11px] text-muted-foreground mb-2">
                        {user?.email} · <span className="font-mono">{user?.role}</span>
                    </div>
                    <button
                        data-testid="logout-btn"
                        onClick={() => logout()}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md border border-border hover:bg-secondary transition-colors"
                    >
                        <LogOut className="size-3.5" /> Sign out
                    </button>
                </div>
            </aside>
            <main className="flex-1 min-w-0 flex flex-col">
                <header className="h-12 border-b border-border flex items-center justify-between px-5 bg-[hsl(240,3%,4%)]">
                    <div className="flex items-center gap-2">
                        <span className="size-1.5 rounded-full bg-primary animate-pulse" />
                        <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                            Live
                        </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono">
                        MOCK_MODE={mockMode ? "true" : "false"}
                    </div>
                </header>
                <div className="flex-1 overflow-y-auto p-5">{children}</div>
            </main>
        </div>
    );
}
